import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useSecuritySettings } from '@/hooks/useSecuritySettings';
import { toast } from 'sonner';

/**
 * Monitors user activity and auto-logs out after the configured
 * session timeout period of inactivity.
 */
export const useSessionTimeout = () => {
  const { user, signOut } = useAuth();
  const { settings, loading } = useSecuritySettings();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningShownRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    warningShownRef.current = false;
  }, []);

  const handleLogout = useCallback(async () => {
    clearTimers();
    toast.info('You have been logged out due to inactivity');
    await signOut();
  }, [signOut, clearTimers]);

  const resetTimer = useCallback(() => {
    if (!user || loading) return;

    const timeoutMs = settings.sessionTimeoutMinutes * 60 * 1000;
    // Don't set timers for very long timeouts (> 24h) or very short ones (< 1 min)
    if (timeoutMs > 86400000 || timeoutMs < 60000) return;

    clearTimers();
    warningShownRef.current = false;

    // Show warning 1 minute before logout (or at 80% of timeout for short timeouts)
    const warningTime = Math.max(timeoutMs - 60000, timeoutMs * 0.8);
    warningRef.current = setTimeout(() => {
      if (!warningShownRef.current) {
        warningShownRef.current = true;
        toast.warning('You will be logged out soon due to inactivity', {
          duration: Math.min(timeoutMs - warningTime, 55000),
        });
      }
    }, warningTime);

    timerRef.current = setTimeout(handleLogout, timeoutMs);
  }, [user, loading, settings.sessionTimeoutMinutes, clearTimers, handleLogout]);

  useEffect(() => {
    if (!user || loading) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    
    // Throttle to avoid excessive resets
    let lastReset = Date.now();
    const throttledReset = () => {
      const now = Date.now();
      if (now - lastReset > 30000) { // Only reset every 30s
        lastReset = now;
        resetTimer();
      }
    };

    events.forEach(event => window.addEventListener(event, throttledReset, { passive: true }));
    resetTimer(); // Start initial timer

    return () => {
      events.forEach(event => window.removeEventListener(event, throttledReset));
      clearTimers();
    };
  }, [user, loading, resetTimer, clearTimers]);
};
